'use client';

import {
  Button,
  FilterBar,
  type FilterConfig,
  type FilterValue,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import {
  GitForkIcon,
  LayoutGridIcon,
  LeftToRightListBulletIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo } from 'react';
import { AI_COMPONENT_TYPE_KEYS } from '../utils/aiComponentTypeLabels';
import { hasMcpAssetsWithSomeAccess } from '../utils/filterAiAssetComponents';
import {
  countAgentComponents,
  countComponentsTabListItems,
  isComponentsTabVisibleItem,
} from '../utils/isUserFacingAgent';
import type {
  AgentViewMode,
  AssetFilter,
  ComponentFilterState,
} from '../types';

export function AiApplicationAssetsToolbar({
  selectedFilter,
  components,
  endpointCount = 0,
  componentFilters,
  agentViewMode,
  isMcpApplication = false,
  hideMcpProtocolMethods,
  onAgentViewModeChange,
  onComponentFiltersChange,
  onSelectFilter,
}: {
  selectedFilter: AssetFilter;
  components: AiComponentDto[];
  endpointCount?: number;
  componentFilters: ComponentFilterState;
  agentViewMode: AgentViewMode;
  isMcpApplication?: boolean;
  hideMcpProtocolMethods?: boolean;
  onAgentViewModeChange: (viewMode: AgentViewMode) => void;
  onComponentFiltersChange: (filters: ComponentFilterState) => void;
  onSelectFilter?: (filter: AssetFilter) => void;
}) {
  const t = useTranslations('aiApplications.assets');
  const tComponentTypes = useTranslations('aiApplications.componentTypes');
  const agentCount = useMemo(
    () => countAgentComponents(components),
    [components],
  );
  const supportingComponentCount = useMemo(
    () =>
      countComponentsTabListItems({
        components,
        isMcpApplication,
        hideMcpProtocolMethods,
      }),
    [components, hideMcpProtocolMethods, isMcpApplication],
  );
  const componentOnlyItems = useMemo(
    () =>
      components.filter((component) =>
        isComponentsTabVisibleItem({
          component,
          isMcpApplication,
          hideMcpProtocolMethods,
        }),
      ),
    [components, hideMcpProtocolMethods, isMcpApplication],
  );
  const hasVaryingAccess = useMemo(
    () => isMcpApplication && hasMcpAssetsWithSomeAccess(components),
    [components, isMcpApplication],
  );

  useEffect(() => {
    if (hasVaryingAccess || !componentFilters.access) {
      return;
    }

    onComponentFiltersChange({
      ...componentFilters,
      access: '',
    });
  }, [componentFilters, hasVaryingAccess, onComponentFiltersChange]);
  const filterConfigs = useMemo<FilterConfig[]>(() => {
    const typeItems = Array.from(
      new Set(componentOnlyItems.map((component) => component.type)),
    ).map((type) => ({
      label: tComponentTypes(AI_COMPONENT_TYPE_KEYS[type]),
      value: type,
    }));
    const methodItems = Array.from(
      new Set(
        componentOnlyItems.flatMap((component) =>
          component.interfaceType ? [component.interfaceType] : [],
        ),
      ),
    ).map((method) => ({ label: method, value: method }));
    const typeFilter: FilterConfig[] = [
      {
        id: 'type',
        placeholder: t('filters.type'),
        items: typeItems,
        disabled: typeItems.length === 0,
      },
    ];
    const sharedFilters: FilterConfig[] = isMcpApplication
      ? typeFilter
      : [
          ...typeFilter,
          {
            id: 'coverage',
            placeholder: t('filters.coverage'),
            items: [
              { label: t('internal'), value: 'internal' },
              { label: t('external'), value: 'external' },
            ],
          },
        ];
    const methodFilter: FilterConfig[] =
      !isMcpApplication && methodItems.length > 1
        ? [
            {
              id: 'method',
              placeholder: t('filters.method'),
              items: methodItems,
            },
          ]
        : [];
    const accessFilter: FilterConfig[] = hasVaryingAccess
      ? [
          {
            id: 'access',
            placeholder: t('filters.access'),
            items: [
              { label: t('accessAllIdentities'), value: 'ALL' },
              { label: t('accessSomeIdentities'), value: 'SOME' },
            ],
          },
        ]
      : [];

    return [...sharedFilters, ...methodFilter, ...accessFilter];
  }, [
    componentOnlyItems,
    hasVaryingAccess,
    isMcpApplication,
    t,
    tComponentTypes,
  ]);
  const filterValue = useMemo<FilterValue>(
    () =>
      [
        componentFilters.type
          ? { id: 'type', value: [componentFilters.type] }
          : null,
        componentFilters.coverage
          ? { id: 'coverage', value: [componentFilters.coverage] }
          : null,
        componentFilters.method
          ? { id: 'method', value: [componentFilters.method] }
          : null,
        componentFilters.access && hasVaryingAccess
          ? { id: 'access', value: [componentFilters.access] }
          : null,
      ].filter((item): item is FilterValue[number] => Boolean(item)),
    [componentFilters, hasVaryingAccess],
  );
  const handleFilterChange = useCallback(
    (value: FilterValue) => {
      const valuesById = Object.fromEntries(
        value.map((item) => [item.id, item.value[0] ?? '']),
      );

      onComponentFiltersChange({
        ...componentFilters,
        type: valuesById.type ?? '',
        coverage: valuesById.coverage ?? '',
        method: valuesById.method ?? '',
        access: valuesById.access ?? '',
        role: '',
        codeCoverage: '',
      });
    },
    [componentFilters, onComponentFiltersChange],
  );
  const handleSearch = useCallback(
    (search: string) => {
      onComponentFiltersChange({ ...componentFilters, search });
    },
    [componentFilters, onComponentFiltersChange],
  );

  return (
    <HStack
      w="full"
      minH="44px"
      justifyContent="space-between"
      px={8}
      py={1.5}
      gap={3}
      borderBottomWidth="1px"
      borderColor="border.neutral.subtle"
    >
      <HStack gap={3} minW={0} flex={1}>
        {!isMcpApplication ? (
          <HStack
            gap={0}
            bg="bg.neutral.subtle"
            borderWidth="1px"
            borderColor="border.neutral.subtle"
            borderRadius="full"
            px={0.5}
            flexShrink={0}
          >
            {(['agents', 'endpoints', 'components'] as const).map((filter) => (
              <Button
                key={filter}
                size="xs"
                variant="ghost"
                borderRadius="full"
                bg={
                  selectedFilter === filter ? 'bg.accent.light' : 'transparent'
                }
                color={
                  selectedFilter === filter ? 'fg.accent.default' : 'fg.muted'
                }
                fontWeight="medium"
                onClick={() => onSelectFilter?.(filter)}
              >
                {filter === 'agents'
                  ? `${t('agents')}${agentCount > 0 ? ` (${agentCount})` : ''}`
                  : filter === 'endpoints'
                    ? `${t('endpoints')}${endpointCount > 0 ? ` (${endpointCount})` : ''}`
                    : `${t('components')}${supportingComponentCount > 0 ? ` (${supportingComponentCount})` : ''}`}
              </Button>
            ))}
          </HStack>
        ) : null}

        {selectedFilter === 'components' && (
          <Box flex={1} minW={0}>
            <FilterBar
              filters={filterConfigs}
              value={filterValue}
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              searchPlaceholder={t('searchComponents')}
              searchValue={componentFilters.search}
              showBottomBorder={false}
              px={0}
              pb={0}
            />
          </Box>
        )}
      </HStack>

      <HStack gap={2.5} flexShrink={0}>
        {selectedFilter === 'agents' && !isMcpApplication ? (
          <HStack
            gap={0}
            bg="bg.neutral.subtle"
            borderWidth="1px"
            borderColor="border.neutral.subtle"
            borderRadius="full"
            px={0.5}
          >
            <Tooltip title={t('cardView')}>
              <Button
                size="xs"
                variant="ghost"
                borderRadius="full"
                aria-label={t('cardView')}
                bg={
                  agentViewMode === 'cards' ? 'bg.accent.light' : 'transparent'
                }
                color={
                  agentViewMode === 'cards' ? 'fg.accent.default' : 'fg.muted'
                }
                onClick={() => onAgentViewModeChange('cards')}
              >
                <LayoutGridIcon size={14} />
              </Button>
            </Tooltip>
            <Tooltip title={t('listView')}>
              <Button
                size="xs"
                variant="ghost"
                borderRadius="full"
                aria-label={t('listView')}
                bg={
                  agentViewMode === 'list' ? 'bg.accent.light' : 'transparent'
                }
                color={
                  agentViewMode === 'list' ? 'fg.accent.default' : 'fg.muted'
                }
                onClick={() => onAgentViewModeChange('list')}
              >
                <LeftToRightListBulletIcon size={14} />
              </Button>
            </Tooltip>
          </HStack>
        ) : null}
        {!isMcpApplication ? (
          <Button
            size="xs"
            variant="outline"
            color="fg.accent.default"
            borderColor="border.accent.default"
            bg={selectedFilter === 'graph' ? 'bg.accent.light' : 'bg.canvas'}
            aria-pressed={selectedFilter === 'graph'}
            _hover={{
              color: 'fg.accent.default',
              borderColor: 'border.accent.default',
            }}
            onClick={() => onSelectFilter?.('graph')}
          >
            <GitForkIcon />
            {t('viewGraph')}
          </Button>
        ) : null}
      </HStack>
    </HStack>
  );
}
